import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { PageHeader } from '../components/ui/PageHeader';
import billingApi from '../services/billingApi';
import { formatPlanPrice } from '../services/planUpgradeBanner';
import {
  CheckCircle, Crown, Building2, Home, FileText, Sparkles,
  Loader2, AlertCircle, Download, ArrowRight, Users
} from 'lucide-react';

export default function Pricing() {
  const navigate = useNavigate();
  const { user, activeCondoId } = useAuth();
  const [entitlements, setEntitlements] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [unitCount, setUnitCount] = useState(20);
  const [condoPricing, setCondoPricing] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  const userRole = user?.role;
  const isCondoAdmin = userRole === 'admin' || userRole === 'super_admin';
  const isResident = userRole === 'resident' || userRole === 'directive';

  const fetchEntitlements = useCallback(async () => {
    try {
      const data = await billingApi.getEntitlements(activeCondoId);
      setEntitlements(data);
    } catch (err) {
      console.error('Error fetching entitlements:', err);
    }
  }, [activeCondoId]);

  const fetchPlans = useCallback(async () => {
    try {
      const data = await billingApi.getPlans();
      setPlans(data.plans || []);
    } catch (err) {
      console.error('Error fetching plans:', err);
    }
  }, []);

  const fetchCondoPricing = useCallback(async (units: number) => {
    try {
      const data = await billingApi.getPlans(units);
      setCondoPricing(data);
    } catch (err) {
      console.error('Error fetching condo pricing:', err);
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchEntitlements(), fetchPlans()])
      .finally(() => setLoading(false));
  }, [fetchEntitlements, fetchPlans]);

  useEffect(() => {
    if (isCondoAdmin) {
      const timer = setTimeout(() => fetchCondoPricing(unitCount), 300);
      return () => clearTimeout(timer);
    }
  }, [unitCount, isCondoAdmin, fetchCondoPricing]);

  const handleStartTrial = async (planId?: string, condoId?: string) => {
    setError(null);
    setActionLoading(true);
    try {
      await billingApi.startTrial(planId || 'home_plus', condoId);
      navigate('/billing');
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Error al iniciar la prueba. Intenta de nuevo.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckout = (planId: string, condoId?: string) => {
    const params = new URLSearchParams({ plan: planId });
    if (condoId) params.set('condoId', condoId);
    navigate(`/billing/checkout?${params.toString()}`);
  };

  const handleDownloadQuote = () => {
    // Generate a simple printable quote
    const quote = generateQuoteHTML(unitCount, condoPricing);
    const blob = new Blob([quote], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `cotizacion-vecindus-${unitCount}-unidades.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleRequestAdmin = () => {
    const message = `Hola, soy residente de tu condominio en Vecindus. Me gustaría solicitar que el condominio contrate un plan de Vecindus para que todos los residentes tengamos acceso completo. Puedes ver los planes en https://vecindus.com/pricing`;
    if (navigator.share) {
      navigator.share({ title: 'Solicitar plan Vecindus', text: message });
    } else {
      navigator.clipboard.writeText(message);
      alert('Mensaje copiado al portapapeles. Compártelo con tu administrador.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-full flex items-center justify-center bg-[var(--v-surface-2)]">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--v-teal-700)]" />
      </div>
    );
  }

  const currentPlan = entitlements?.tier || 'free';
  const condoHasSubscription = entitlements?.condo_subscription === true;

  // 2026-09-03 (Jaime): /pricing limit numbers are DB-driven from
  // plan_definitions.limits (GET /api/billing/plans exposes `limits` per plan,
  // editable at /admin/pricing). Missing key or missing plan -> static fallback
  // copy so the UI never blanks on API hiccups or pre-migration-89 payloads.
  const planById = (id: string) => plans.find(p => p?.id === id);
  const limitStr = (id: string, key: string): string | null => {
    const limits = planById(id)?.limits;
    if (!limits || limits[key] === undefined) return null;
    const n = Number(limits[key]);
    if (limits[key] === null || !Number.isFinite(n) || n < 0) return 'Ilimitadas';
    return String(Math.floor(n));
  };
  const freeTaskLimit = limitStr('free', 'home-tasks') ?? '5';
  const freePetLimit = limitStr('free', 'home-pets') ?? '1';
  const homeTaskLimit = limitStr('home_plus', 'home-tasks');
  const homePetLimit = limitStr('home_plus', 'home-pets');
  const condoPlanId = planById('condo_small') ? 'condo_small'
    : (plans.find((p: any) => typeof p?.id === 'string' && p.id.startsWith('condo_') && p.id !== 'condo_free')?.id || 'condo_free');
  const cell = (planId: string, key: string, fallback: string) => limitStr(planId, key) ?? fallback;

  return (
    <div className="min-h-full bg-[var(--v-surface-2)] text-[var(--v-text-primary)]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <PageHeader
          title="Planes y Precios"
          subtitle="Elige el plan ideal para tu hogar o condominio"
        />

        {error && (
          <div className="mb-6 p-4 rounded-[var(--v-radius-md)] bg-red-50 border border-red-200 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}

        {/* Resident without condo subscription */}
        {isResident && !condoHasSubscription && currentPlan === 'free' && (
          <Card className="mb-8 p-6 border-l-4 border-l-amber-500 bg-amber-50">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Building2 className="w-10 h-10 text-amber-600 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="font-bold text-amber-900">Tu condominio no tiene plan activo</h3>
                <p className="text-sm text-amber-700 mt-1">
                  Puedes mejorar tu cuenta individualmente a Home+, o solicitar a tu administrador que contrate un plan para todo el edificio.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Button variant="outline" size="sm" onClick={handleRequestAdmin}>
                  Solicitar a mi administrador
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Condo Admin view with unit slider */}
        {isCondoAdmin && (
          <Card className="mb-8 p-6">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--v-teal-700)]" />
              Calcula el precio de tu condominio
            </h3>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-1 w-full">
                <label className="text-sm text-[var(--v-text-secondary)] mb-2 block">
                  Número de unidades: <span className="font-bold text-[var(--v-text-primary)]">{unitCount}</span>
                </label>
                <input
                  type="range"
                  min={1}
                  max={500}
                  step={1}
                  value={unitCount}
                  onChange={(e) => setUnitCount(Number(e.target.value))}
                  className="w-full accent-[var(--v-teal-700)]"
                />
                <div className="flex justify-between text-xs text-[var(--v-text-secondary)] mt-1">
                  <span>1</span>
                  <span>300+</span>
                </div>
              </div>
              <div className="text-center sm:text-right">
                <div className="text-2xl font-extrabold text-[var(--v-teal-700)]">
                  {formatPlanPrice(condoPricing?.recommendedAmountUsd)}
                  <span className="text-sm font-normal text-[var(--v-text-secondary)]"> USD/mes</span>
                </div>
                <div className="text-xs text-[var(--v-text-secondary)]">
                  <span className="text-[var(--v-teal-700)]">{condoPricing?.recommended_tier || 'Calculando nivel…'}</span>
                </div>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <Button
                variant="primary"
                onClick={() => handleStartTrial(undefined, user?.condoId)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Comenzar prueba gratis (30 días)
              </Button>
              <Button variant="outline" onClick={handleDownloadQuote}>
                <Download className="w-4 h-4 mr-2" />
                Descargar cotización (PDF)
              </Button>
            </div>
          </Card>
        )}

        {/* Home+ Plan (always visible, prominent for non-condo-admin) */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Free Plan */}
          <Card className="flex flex-col p-6 h-full">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Home className="w-5 h-5 text-[var(--v-text-secondary)]" />
                <h3 className="text-xl font-bold">Gratis</h3>
              </div>
              <p className="text-sm text-[var(--v-text-secondary)]">Para empezar a organizar tu hogar</p>
            </div>
            <div className="text-3xl font-extrabold mb-6">
              $0<span className="text-base font-normal text-[var(--v-text-secondary)]">/mes</span>
            </div>
            <ul className="flex-1 space-y-3 mb-6">
              <FeatureItem text="Calendario familiar" />
              <FeatureItem text="Lista de compras" />
              <FeatureItem text={`Hasta ${freeTaskLimit} tareas`} />
              <FeatureItem text={`${freePetLimit} mascota${freePetLimit === '1' ? '' : 's'}`} />
              <FeatureItem text="Código QR de acceso" />
            </ul>
            {currentPlan === 'free' && (
              <Button variant="secondary" fullWidth disabled>Plan actual</Button>
            )}
          </Card>

          {/* Home+ Plan */}
          <Card className={`flex flex-col p-6 h-full relative overflow-hidden ${currentPlan !== 'home_plus' ? 'border-2 border-[var(--v-teal-700)] shadow-[var(--v-shadow-md)]' : ''}`}>
            {currentPlan !== 'home_plus' && (
              <div className="absolute top-0 right-0 bg-[var(--v-teal-700)] text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                RECOMENDADO
              </div>
            )}
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-5 h-5 text-[var(--v-teal-700)]" />
                <h3 className="text-xl font-bold text-[var(--v-teal-700)]">Home+</h3>
              </div>
              <p className="text-sm text-[var(--v-text-secondary)]">Todo para tu hogar, sin límites</p>
            </div>
            <div className="text-3xl font-extrabold mb-1">
              $3.99<span className="text-base font-normal text-[var(--v-text-secondary)]">/mes</span>
            </div>
            <p className="text-sm text-[var(--v-teal-700) font-medium mb-4">1 mes de prueba gratis, sin tarjeta</p>
            <ul className="flex-1 space-y-3 mb-6">
              <FeatureItem text={homeTaskLimit ? `Hasta ${homeTaskLimit} tareas` : 'Tareas ilimitadas'} highlighted />
              <FeatureItem text={homePetLimit ? `${homePetLimit} ${homePetLimit === '1' ? 'mascota' : 'mascotas'}` : 'Mascotas ilimitadas'} highlighted />
              <FeatureItem text="Vehículos y mantenimiento" highlighted />
              <FeatureItem text="Presupuesto familiar" highlighted />
              <FeatureItem text="Seguridad del hogar" highlighted />
              <FeatureItem text="Todo lo del plan Gratis" highlighted />
            </ul>
            {currentPlan === 'home_plus' ? (
              <Button variant="secondary" fullWidth disabled>Plan actual</Button>
            ) : currentPlan === 'free' ? (
              <Button
                variant="primary"
                fullWidth
                onClick={() => handleStartTrial('home_plus')}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Comenzar prueba gratis
              </Button>
            ) : (
              <Button
                variant="primary"
                fullWidth
                onClick={() => handleCheckout('home_plus')}
              >
                Mejorar a Home+
              </Button>
            )}
          </Card>

          {/* Condo Plan */}
          <Card className="flex flex-col p-6 h-full">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="w-5 h-5 text-[var(--v-text-secondary)]" />
                <h3 className="text-xl font-bold">Condominio</h3>
              </div>
              <p className="text-sm text-[var(--v-text-secondary)]">Para todo el edificio</p>
            </div>
            <div className="text-3xl font-extrabold mb-1">
              Desde $15<span className="text-base font-normal text-[var(--v-text-secondary)]">/mes</span>
            </div>
            <p className="text-sm text-[var(--v-text-secondary)] mb-4">Precio según número de unidades</p>
            <ul className="flex-1 space-y-3 mb-6">
              <FeatureItem text="Todos los residentes con acceso completo" />
              <FeatureItem text="Gestión de unidades" />
              <FeatureItem text="Módulos de finanzas y morosos" />
              <FeatureItem text="Control de accesos QR" />
              <FeatureItem text="Votaciones y anuncios" />
              <FeatureItem text="Personal de seguridad" />
            </ul>
            {isCondoAdmin ? (
              <Button
                variant="outline"
                fullWidth
                onClick={() => handleStartTrial(undefined, user?.condoId)}
                disabled={actionLoading}
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Comenzar prueba gratis (30 días)
              </Button>
            ) : (
              <Button variant="ghost" fullWidth onClick={handleRequestAdmin}>
                Solicitar a mi administrador
              </Button>
            )}
          </Card>
        </div>

        {/* Feature comparison table */}
        <Card className="p-6 overflow-x-auto">
          <h3 className="text-lg font-bold mb-4">Comparación de características</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--v-border-light)]">
                <th className="text-left py-3 pr-4 font-semibold text-[var(--v-text-secondary)]">Característica</th>
                <th className="text-center py-3 px-4 font-semibold">Gratis</th>
                <th className="text-center py-3 px-4 font-semibold text-[var(--v-teal-700)]">Home+</th>
                <th className="text-center py-3 px-4 font-semibold">Condominio</th>
              </tr>
            </thead>
            <tbody>
              <ComparisonRow feature="Calendario familiar" free="✓" home="✓" condo="✓" />
              <ComparisonRow feature="Lista de compras" free={cell('free', 'home-shopping', '20')} home={cell('home_plus', 'home-shopping', 'Ilimitadas')} condo={cell(condoPlanId, 'home-shopping', 'Ilimitadas')} />
              <ComparisonRow feature="Tareas" free={cell('free', 'home-tasks', '5')} home={cell('home_plus', 'home-tasks', 'Ilimitadas')} condo={cell(condoPlanId, 'home-tasks', 'Ilimitadas')} />
              <ComparisonRow feature="Mascotas" free={cell('free', 'home-pets', '1')} home={cell('home_plus', 'home-pets', 'Ilimitadas')} condo={cell(condoPlanId, 'home-pets', 'Ilimitadas')} />
              <ComparisonRow feature="Residentes por unidad" free={cell('free', 'residents', '3')} home={cell('home_plus', 'residents', '10')} condo={cell(condoPlanId, 'residents', '10')} />
              <ComparisonRow feature="Personal" free={cell('free', 'personal', '1')} home={cell('home_plus', 'personal', '5')} condo={cell(condoPlanId, 'personal', '5')} />
              <ComparisonRow feature="Vehículos" free={cell('free', 'home-vehicles', '—')} home={cell('home_plus', 'home-vehicles', '✓')} condo={cell(condoPlanId, 'home-vehicles', '✓')} />
              <ComparisonRow feature="Presupuesto familiar" free={cell('free', 'home-budget', '—')} home={cell('home_plus', 'home-budget', '✓')} condo={cell(condoPlanId, 'home-budget', '✓')} />
              <ComparisonRow feature="Seguridad del hogar" free="Básico" home="✓" condo="✓" />
              <ComparisonRow feature="Gestión de unidades" free="—" home="—" condo="✓" />
              <ComparisonRow feature="Finanzas del condominio" free="—" home="—" condo="✓" />
              <ComparisonRow feature="Control de morosos" free="—" home="—" condo="✓" />
              <ComparisonRow feature="Personal de seguridad" free="—" home="—" condo="✓" />
              <ComparisonRow feature="Votaciones avanzadas" free="—" home="—" condo="✓" />
                          </tbody>
          </table>
        </Card>

        {/* Trust badges */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-[var(--v-text-secondary)]">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[var(--v-success)]" />
            Sin tarjeta para prueba gratuita
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[var(--v-success)]" />
            Cancela cuando quieras
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[var(--v-success)]" />
            Pago seguro procesado por Openpay
          </div>
        </div>
      </div>
    </div>
  );
}

function FeatureItem({ text, highlighted }: { text: string; highlighted?: boolean }) {
  return (
    <li className="flex items-center gap-2 text-sm">
      <CheckCircle className={`w-4 h-4 flex-shrink-0 ${highlighted ? 'text-[var(--v-teal-700)]' : 'text-[var(--v-teal-400)]'}`} />
      <span className={highlighted ? 'font-medium' : ''}>{text}</span>
    </li>
  );
}

function ComparisonRow({ feature, free, home, condo }: { feature: string; free: string; home: string; condo: string }) {
  return (
    <tr className="border-b border-[var(--v-border-light)] last:border-0">
      <td className="py-3 pr-4 text-[var(--v-text-primary)]">{feature}</td>
      <td className="text-center py-3 px-4 text-[var(--v-text-secondary)]">{free}</td>
      <td className="text-center py-3 px-4 font-medium text-[var(--v-teal-700)]">{home}</td>
      <td className="text-center py-3 px-4 text-[var(--v-text-primary)]">{condo}</td>
    </tr>
  );
}

function generateQuoteHTML(units: number, pricing: any): string {
  const amountUSD = formatPlanPrice(pricing?.recommendedAmountUsd).replace(/^\$/, '');
  
  const date = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
  const validUntil = new Date(Date.now() + 30 * 86400000).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });

  return `<!DOCTYPE html>
<html lang="es">
<head>
<meta charset="UTF-8">
<title>Cotización Vecindus - ${units} unidades</title>
<style>
body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 40px auto; padding: 20px; color: #1a202c; }
h1 { color: #0f766e; }
table { width: 100%; border-collapse: collapse; margin: 20px 0; }
td, th { padding: 12px; text-align: left; border-bottom: 1px solid #e2e8f0; }
.total { font-size: 24px; font-weight: bold; color: #0f766e; }
</style>
</head>
<body>
<h1>Cotización Vecindus</h1>
<p>Fecha: ${date}</p>
<p>Válido hasta: ${validUntil}</p>
<table>
<tr><th>Concepto</th><th>Detalle</th></tr>
<tr><td>Plan</td><td>${pricing?.recommended_tier || 'Condominio'}</td></tr>
<tr><td>Unidades</td><td>${units}</td></tr>
<tr><td>Precio mensual (USD)</td><td>$${amountUSD} USD</td></tr>

<tr><td>Período de prueba</td><td>30 días gratis, sin tarjeta</td></tr>
</table>
<p class="total">Total mensual: $${amountUSD} USD</p>
<p style="margin-top: 30px; color: #64748b; font-size: 14px;">
Para activar este plan, visita https://vecindus.com/pricing o contáctanos en soporte@vecindus.com
</p>
</body>
</html>`;
}
